<template>
  <div class="cases-container">
    <toolbar class="md-primary md-elevation-6" title="Scenarios" :has-menu="false" :has-search="showSearch" @search="updateSearchTerm">
      <div v-if="showFilters" class="filters-dropdown">
        <md-field>
          <md-select v-model="state.filterCriteria" placeholder="Filters" name="filterCriteria">
            <md-option v-for="filter in filters" :key="filter.key" :value="filter.key">{{ filter.label }}</md-option>
            <md-option :value="0">No filter</md-option>
          </md-select>
        </md-field>
      </div>
    </toolbar>

    <md-progress-bar v-if="loading || state.updating" md-mode="indeterminate" />

    <md-content v-if="state.updateError" class="md-accent update-error">{{ state.updateError.message }}</md-content>

    <header-row
      :user="user"
      :sort-by="state.sortBy"
      :show-favorites="showFavorites"
      :show-editor="showColumnEditor && !isStudent"
      :displayed-columns="columnsVisibleToUser"
      :show-update-column="needsEcUpdates"
      class="header md-elevation-6"
      @updateSortBy="field => updateSortBy(field)"
      @updateDisplayedColumns="columns => updateDisplayedColumns(columns)"
    />

    <div v-if="isSticky && previewedCaseId" class="white bg md-elevation-6">
      <case-card
        :key="previewedCaseId"
        :selected="true"
        :case-conf="previewedCase"
        :origin="origin => getCaseOrigin(previewedCase)"
        :removable="isRemovable(previewedCase)"
        :show-favorites="showFavorites"
        :show-spacer="showColumnEditor"
        :show-update-column="needsEcUpdates"
        :displayed-columns="columnsVisibleToUser"
        :update-status="state.updateStatuses[previewedCaseId]"
        @click.native="previewCase(previewedCase)"
        @favorite="caseConf => toggleFavorite(previewedCaseId)"
      />
    </div>

    <md-content
      class="case-card-container container md-scrollbar white bg md-elevation-6"
      :class="{ 'case-card-container-short': isSticky, 'has-pending-updates': hasPendingUpdates }"
    >
      <md-list
        v-if="!fetchErrors.length"
        v-infinite-scroll="loadMoreCases"
        infinite-scroll-disabled="infiniteScrollDisabled"
        :infinite-scroll-distance="200"
      >
        <case-card
          v-for="caseConf in filteredData"
          :key="caseConf.id"
          :selected="caseConf.id === previewedCaseId"
          :case-conf="caseConf"
          :origin="() => getCaseOrigin(caseConf)"
          :owner="() => getCaseOwner(caseConf)"
          :removable="isRemovable(caseConf)"
          :show-favorites="showFavorites"
          :show-spacer="showColumnEditor"
          :displayed-columns="columnsVisibleToUser"
          :show-update-column="needsEcUpdates"
          :update-status="state.updateStatuses[caseConf.id]"
          @click.native="previewCase(caseConf)"
          @favorite="caseConf => toggleFavorite(caseConf.id)"
          @updateStatusChange="(caseConf, status) => changeUpdateStatus(caseConf, status)"
        />
      </md-list>

      <md-empty-state
        v-if="fetchErrors.length"
        class="md-accent"
        md-icon="error"
        md-label="Error when loading cases"
        :md-description="fetchErrors[0] ? fetchErrors[0].message : 'Unknown error'"
      />
    </md-content>

    <md-toolbar v-if="hasPendingUpdates" class="update-confirmation-bar gray-6 bg md-elevation-6">
      <div>
        <span class="confirm-title">Confirm selection(s) ? </span>
        <span class="confirm-subtitle">Updating is permanent. Declined updates can be updated in Medications panel or Case Logic panel.</span>
      </div>
      <md-button class="confirm-update-button" type="primary" size="large" :disabled="state.updating" @click="confirmUpdates()">
        Confirm
      </md-button>
    </md-toolbar>
    <md-toolbar
      v-if="showBottomToolbar"
      class="md-primary empty-toolbar bottom-toolbar md-elevation-6"
    />
  </div>
</template>

<script>
import { get, findIndex, clone, assign, mapValues, find, fromPairs, keyBy } from 'lodash';

import Toolbar from '~lib/common/components/Toolbar';
import { groupLabel, userLabel } from '~lib/common/utils/labels';
import templateOrgs from '~lib/common/config/templateOrgs';

import CaseCard from './CaseCard.vue';
import HeaderRow from './HeaderRow.vue';

import caseColumns from './caseColumns';

export default {
  name: 'case-list',
  components: {
    CaseCard,
    Toolbar,
    HeaderRow,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      required: true,
    },
    getUserPref: {
      type: Function,
      required: true,
    },
    setUserPref: {
      type: Function,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fetchErrors: {
      type: Array,
      default: () => [],
    },
    orgData: {
      type: Object,
      default: () => {},
    },
    showBottomToolbar: {
      type: Boolean,
      default: true,
    },
    showTemplateCases: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    showColumnEditor: {
      type: Boolean,
      default: true,
    },
    showFavorites: {
      type: Boolean,
      default: true,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    previewedCase: {
      type: Object,
      default: () => null,
    },
    initialCaseId: {
      type: String,
      default: null,
    },
    validateCaseConfById: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      caseColumns,
      templateOrgsByProduct: keyBy(templateOrgs, 'productType'),
      state: {
        sortBy: {
          field: 'lastAccessed',
          desc: true,
        },
        displayedColumns: caseColumns.slice(0, 4),
        searchTerm: '',
        filterCriteria: null,
        updateStatuses: {},
        updating: false,
        updateError: null,
      },
    };
  },
  computed: {
    previewedCaseId() {
      return this.previewedCase ? this.previewedCase.id : null;
    },
    infiniteScrollDisabled() {
      return this.loading || this.endReached;
    },
    filters() {
      return [{
        key: 'mine',
        label: 'Made by me',
        value: {
          access: { owner: { eq: this.user.id } }
        }
      }, {
        key: 'shared',
        label: 'Shared with me',
        value: {
          access: { owner: { neq: this.user.id } }
        }
      }, {
        key: 'assigned',
        label: 'Assigned to me',
        value: {
          timed_access: {
            or: [{
              users: {
                contains: this.user.id
              },
            }, {
              groups: {
                intersects: this.user.groups,
              }
            }]
          }
        }
      }, {
        key: 'favorited',
        label: 'Favorited',
        value: {
          isFavorite: { eq: true }
        }
      }, {
        key: 'template',
        label: 'VES Template',
        value: {
          access: {ownerOrg: {eq: this.templateOrgId}}
        }
      }]
    },
    // actively filter the list as the user types before even hitting
    // the server
    filteredData() {
      let predicates = [];
      if (this.state.searchTerm) {
        predicates.push((caseConf) =>
          caseConf.searchableText
            .toLowerCase()
            .includes(this.state.searchTerm.trim().toLowerCase())
        );
      }
      if (this.isSticky) {
        predicates.push((caseConf) => caseConf.id !== this.previewedCaseId);
      }
      if (!this.showTemplateCases) {
        predicates.push((caseConf) => caseConf.access.ownerOrg != this.templateOrgId);
      }
      if (predicates.length === 0) {
        return this.data;
      } else {
        return this.data.filter(caseConf => {
          for (const predicate of predicates) {
            if (!predicate(caseConf)) {
              return false;
            }
          }
          return true;
        });
      }
    },
    isStudent() {
      return this.user['https://vrpatients.com/role'] === 'STUDENT';
    },
    columnsVisibleToUser() {
      return this.isStudent ? [] : this.state.displayedColumns;
    },
    needsEcUpdates() {
      return this.data.some(caseConf => caseConf.needsEcUpdate);
    },
    hasPendingUpdates() {
      return find(this.state.updateStatuses, value => value === 'YES' || value === 'NO');
    },
    templateOrgId() {
      const productType = this.user['https://vrpatients.com/productType'];
      return this.templateOrgsByProduct[productType].id;
    }
  },
  watch: {
    fetchErrors(newValue, oldValue) {
      if (oldValue !== newValue) {
        this.$emit('fetchErrorsChanged', newValue);
      }
    },
    'state.filterCriteria'(newValue) {
      this.updateFilter(newValue);
    }
  },
  created() {
    const displayedFieldsPref = this.getUserPref('caseListDisplayedFields');
    if (displayedFieldsPref) {
      this.state.displayedColumns = displayedFieldsPref
        .map(field => caseColumns.find(c => c.field === field))
        .filter(v => !!v);
    }
  },
  mounted() {
    this.$eventHub.$on('CASE::REMOVE', caseConf => {
      this.removeCase(caseConf.id);
    });
    this.$eventHub.$on('CASE::DUPLICATE', (caseConf, newCaseName) => {
      this.duplicateCase(caseConf.id, newCaseName);
    });
    this.$eventHub.$on('CASE::FAVORITE', caseConf => {
      this.toggleFavorite(caseConf.id);
    });
    this.$eventHub.$on('CASE::UPDATE', caseConf => {
      this.updateCaseConf(caseConf);
    });
    this.$eventHub.$on('CASE::UPDATE_ALL_EFFECT_CHAINS', (callback) => {
      this.updateAllCases(callback);
    });
    this.openDefaultCase(this.initialCaseId);
    this.$analytics.event('Case Page', 'case_page');
  },
  destroyed() {
    this.$eventHub.$off('CASE::REMOVE');
    this.$eventHub.$off('CASE::DUPLICATE');
    this.$eventHub.$off('CASE::FAVORITE');
    this.$eventHub.$off('CASE::UPDATE');
  },
  methods: {
    loadMoreCases() {
      this.$emit('loadMore');
    },
    previewCase(caseConf) {
      this.$emit('previewCase', caseConf);
      this.$analytics.event('Case Page', 'select_case', caseConf.name);
    },
    removeCase(id) {
      this.$emit('remove', { id }, () => {
        this.$nextTick(() => {
          // Do next tick so that removed case is reflected in computed properties
          if (this.previewedCaseId === id) {
            this.openDefaultCase();
          }
        })
      });
    },
    duplicateCase(id, newCaseName) {
      this.$emit('duplicate', { id, newCaseName }, result => {
        const newCaseId = result.duplicateCases[0];
        this.$emit('loadAndSelect', newCaseId);
      });
    },
    toggleFavorite(id) {
      const index = findIndex(this.data, { id });
      const caseConf = clone(this.data[index]);
      caseConf.isFavorite = !caseConf.isFavorite;
      this.data.splice(index, 1, caseConf);
      this.$emit('previewCase', this.data[index]);

      if (caseConf.isFavorite) {
        this.$emit('addFavorite', { id });
      } else {
        this.$emit('removeFavorite', { id });
      }
    },
    updateCaseConf(updates) {
      const index = findIndex(this.data, { id: updates.id });
      const caseConf = clone(this.data[index]);
      const updatedCaseConf = assign({}, caseConf, updates);
      this.data.splice(index, 1, updatedCaseConf);
      this.$emit('previewCase', this.data[index]);
    },
    isRemovable() {
      return true;
    },
    updateSortBy(field) {
      const { sortBy } = this.state;
      if (this.state.sortBy.field === field) {
        sortBy.desc = !sortBy.desc;
      } else {
        sortBy.field = field;
        sortBy.desc = false;
      }
      this.$emit('sort', `-needsEcUpdate,-errorsCount,${sortBy.desc ? '-' : ''}${sortBy.field}`);
      this.$analytics.event('Case Page', 'sort_case_list', sortBy.field);
    },
    updateFilter(filterKey) {
      if (filterKey === 0) {
        this.$emit('filter', null);
        return;
      }
      const filter = this.filters.find(f => f.key === filterKey);
      this.$emit('filter', filter.value);
      this.$analytics.event('Case Page', 'filter_case_list', filter.label);
    },
    updateSearchTerm(term) {
      this.state.searchTerm = term;
      this.triggerSearch();
    },
    triggerSearch() {
      this.$emit('search', this.state.searchTerm);
    },
    updateDisplayedColumns(columns) {
      this.state.displayedColumns = columns;
      this.setUserPref('caseListDisplayedFields', this.state.displayedColumns.map(column => column.field));
    },
    openDefaultCase(caseId = null) {
      if (this.filteredData && this.filteredData.length > 0) {
        let caseIndex = 0;
        if (caseId) {
          caseIndex = Math.max(this.filteredData.findIndex(caseConf => caseConf.id === caseId), 0);
        }
        this.previewCase(this.filteredData[caseIndex]);
      }
    },
    changeUpdateStatus(caseConf, status) {
      this.state.updateStatuses = {
        ...this.state.updateStatuses,
        [caseConf.id]: status,
      };
    },
    confirmUpdates() {
      this.state.updating = true;
      const caseIdsToUpdate = Object.keys(this.state.updateStatuses)
        .filter(caseId => this.state.updateStatuses[caseId] === 'YES');
      const caseIdsToDecline = Object.keys(this.state.updateStatuses)
        .filter(caseId => this.state.updateStatuses[caseId] === 'NO');
      caseIdsToDecline.forEach(caseIdToDecline => {
        this.$emit('save', {
          id: caseIdToDecline,
          ignoreEcUpdate: true,
        });
      });
      this.updateCases(caseIdsToUpdate);
    },
    updateCases(caseIdsToUpdate, callback) {
      if (!this.validateCaseConfById) {
        console.error('Case validator not provided, cannot update cases');
        return;
      }
      this.state.updating = true;
      this.state.updateError = null;
      this.$emit('updateCasesEffectChains', { caseIds: caseIdsToUpdate }, async (data) => {
        try {
          const updatedCaseIds = data.updateCasesEffectChains;
          const validationResults = await Promise.all(updatedCaseIds.map(caseId => this.validateCaseConfById(caseId)));
          this.state.updateStatuses = {
            ...mapValues(this.state.updateStatuses, status => {
              if (status === 'NO') {
                return 'DECLINED';
              }
              return status;
            }),
            ...fromPairs(updatedCaseIds.map(caseId => [caseId, 'UPDATED'])),
          };
          updatedCaseIds.forEach((caseId, index) => {
            const caseValidationResult = validationResults[index];
            if (caseValidationResult.errors.length) {
              this.state.updateStatuses[caseId] = 'ERRORED';
              this.$emit('save', {
                id: caseId,
                errorsCount: caseValidationResult.errors.length,
              });
            }
          });
          this.state.updating = false;
          if (callback) {
            callback(null);
          }
        } catch (e) {
          console.error(e);
          this.state.updating = false;
          this.state.updateError = e;
          if (callback) {
            callback(e);
          }
        }
      }, (error) => {
        this.state.updating = false;
        this.state.updateError = error;
        if (callback) {
          callback(error);
        }
      });
    },
    updateAllCases(callback) {
      this.updateCases(null, callback);
    },
    getCaseOrigin(caseConf) {
      if (!caseConf || !this.orgData || !this.orgData.groups || !this.orgData.users) {
        return undefined;
      }
      if (caseConf.access.ownerOrg === this.templateOrgId) {
        return 'VRpatients';
      } else if (caseConf.shares) {
        return this.getOriginFromShares(caseConf);
      }
      return this.getOriginFromAccess(caseConf);
    },
    getOriginFromShares(caseConf) {
      let origin;
      const userShare = caseConf.shares.find(
        share => share.shareType === 'user' && share.to === this.user.id,
      );
      if (userShare) {
        const sharingUser = this.orgData.users.find(user => user.user_id === userShare.from);
        if (sharingUser) {
          origin = userLabel(sharingUser);
        }
      } else if (caseConf.access.owner !== this.user.id) {
        caseConf.shares
          .filter(share => share.shareType === 'group')
          .forEach(share => {
            const groupId = share.to;
            if (this.user.groups.includes(groupId)) {
              const group = this.orgData.groups.find(item => item._id === groupId);
              if (group) {
                origin = groupLabel(group);
              }
            }
          });
      }
      return origin;
    },
    getOriginFromAccess(caseConf) {
      let origin;
      const groups = get(caseConf, 'access.run.groups');
      if (groups) {
        groups.forEach(groupId => {
          if (this.user.groups.includes(groupId)) {
            const group = this.orgData.groups.find(item => item._id === groupId);
            if (group) {
              origin = groupLabel(group);
            }
          }
        });
      }
      return origin;
    },
    getCaseOwner(caseConf) {
      const ownerId = caseConf.access.owner;
      if (!ownerId) {
        return 'Unknown';
      }
      const user = this.orgData.users.find(u => u.user_id === ownerId);
      if (user) {
        return userLabel(user);
      }
      if (templateOrgs.find(org => org.id === caseConf.access.ownerOrg)) {
        return 'VRpatients';
      }
      return 'Unknown';
    },
  }
}
</script>

<style scoped>
.cases-container {
  height: 100%;
}

.case-card-container {
  max-height: calc(100% - 160px);
   overflow: auto;
  flex-direction: row;
}

.case-card-container.has-pending-updates {
  max-height: calc(100% - 224px);
}

.case-card-container .md-list {
  padding: 0;
}

.case-card-container-short {
  max-height: calc(100% - 204px);
}

.cases-container .md-progress-bar{
  margin-bottom: -5px;
  z-index: 1;
}

.md-icon:after {
  content: none;
}

.header {
  height: 60px;
}

.filters-dropdown {
  margin-right: 10px;
}

.update-confirmation-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.confirm-title {
  font-size: 18px;
  line-height: 24px;
  text-transform: uppercase;
  display: inline;
}

.confirm-subtitle {
  font-size: 18px;
  color: #aaaaaa;
  display: inline;
}

.confirm-update-button {
  background-color: #62cd63;
  color: #fff;
  text-transform: uppercase;
}

.update-error {
  padding: 10px;
}
</style>
